import React, { useRef } from 'react';
import { dateFormat, promiseStates, ReactNodeGenerator, offsetTime } from '../../../lib/helpers';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor, EditorEvent, Events } from 'tinymce';
import { EssayStudentSceneStore } from './EssayStudentSceneStore';
import { observer } from 'mobx-react';
import { PromiseMessage, routerStore } from 'lib/components';
import { httpClient } from 'domain/utils/httpClient';
import { environment } from 'lib/environment';
import Swal from 'sweetalert2';
import { Rating, Box } from '@mui/material';
import Countdown from 'react-countdown';
import moment from 'moment';
import { EssayStatus } from 'domain/enums';
import { EssayViewModel } from '../EssayModels';
import { routes } from 'pages/Routes';
import { withTranslation, WithTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface Props extends WithTranslation {
    id: number
}

const editorEnabledStatuses: EssayStatus[] = [EssayStatus.New, EssayStatus.InProgress, EssayStatus.Rejected];

@observer
class EssayStudentScene extends React.Component<Props> {
    readonly store: EssayStudentSceneStore;
    private editor: TinyMCEEditor | null = null;
    private t: TFunction;

    constructor(props: Readonly<Props>) {
        super(props);
        this.store = new EssayStudentSceneStore(props.id);
        this.t = props.t;
    }

    componentDidMount() {
        this.store.loadEssay();
    }

    *renderer(): ReactNodeGenerator {
        const state = this.store.essayLoader;
        yield (
            <PromiseMessage key="lesson-loader-state"
                label={this.t('essay.loading')}
                state={state}
                align="right" />);

        if (promiseStates.isRejected(state)) {
            yield (
                <div className="row" key="lesson-load-action">
                    <div className="col-auto ml-auto">
                        <button type="button" className="btn btn-primary"
                            onClick={this.store.loadEssay}>
                            {this.t('essay.load')}
                        </button>
                    </div>
                </div>
            );
        }
        if (state == null || state.isResolved == false) {
            return;
        }

        const essay = state.value;
        let countdownDeadline: Date | undefined = undefined;
        let countdownAutoStart: boolean | undefined = undefined;
        if (essay.StartedAt != null && essay.TimeLimitMinutes != null) {
            if (essay.FinishedAt != null) {
                countdownDeadline = moment().add((essay.TimeLimitMinutes * 60 * 1000) - moment.unix(essay.FinishedAt).diff(moment.unix(essay.StartedAt)), 'millisecond').toDate();
                countdownAutoStart = false;
            } else {
                countdownDeadline = moment.unix(essay.StartedAt).add(essay.TimeLimitMinutes, 'minutes').toDate();
                countdownAutoStart = true;
            }
        }
        const editorEnabled: boolean = essay.StudentSaveAllowed || essay.StudentSubmitAllowed;
        const editorDisabled = editorEnabled == false;
        const showSaveBtn = essay.StudentSaveAllowed;
        const showSubmitBtn = essay.StudentSubmitAllowed;

        yield (
            <div key="EssayStudentScene">
                <h1>{essay.Title}</h1>
                <table className="table">
                    <tbody>
                        <tr>
                            <td>{this.t('essay.table.teacher')} </td>
                            <td>{this.t('essay.table.time-limit')}</td>
                            <td>{this.t('essay.table.time-left')} </td>
                            <td>{this.t('essay.table.rating')} </td>
                            <td>{this.t('essay.table.status')} </td>
                            <td>{this.t('essay.table.updated-at')} </td>
                            <td>{this.t('essay.table.created-at')} </td>
                        </tr>
                        <tr>
                            <td>{essay.TeacherName}</td>
                            <td>{essay.TimeLimitStr}</td>
                            <td>
                                <Countdown date={countdownDeadline} daysInHours={true} autoStart={countdownAutoStart} />
                            </td>
                            <td>
                                <Rating
                                    name="simple-controlled"
                                    value={essay.Rating}
                                    size="large"
                                    readOnly
                                />
                            </td>
                            <td>{essay.StatusStr}</td>
                            <td>{dateFormat(essay.UpdatedAt, "wymdhm", true)}</td>
                            <td>{dateFormat(essay.CreatedAt, "wymdhm", true)}</td>
                        </tr>
                    </tbody>
                </table>
                {essay.Instructions &&
                    <div>
                        <h4>{this.t('essay.instructions')}: </h4>
                        <h6>
                            {essay.Instructions.split('\n').map((item, key) => {
                                return <span key={key}>{item}<br /></span>
                            })}
                        </h6>
                        <div style={{ height: '20px' }}></div>
                    </div>
                }
                {essay.TeacherComment &&
                    <div>
                        <h4>{this.t('essay.teacher-comment')}: </h4>
                        <h6>
                            {essay.TeacherComment.split('\n').map((item, key) => {
                                return <span key={key}>{item}<br /></span>
                            })}
                        </h6>
                        <div style={{ height: '20px' }}></div>
                    </div>
                }

                <Editor
                    onInit={(evt, editor) => { this.onMceInit(editor) }}
                    initialValue={essay.Text ?? undefined}
                    onPaste={this.tinymceOnPaste}
                    disabled={editorDisabled}
                    init={{
                        height: 500,
                        menubar: false,
                        contextmenu: false,
                        contextmenu_never_use_native: true,
                        readonly: editorDisabled,
                        plugins: [
                            'fullscreen',
                        ],
                        toolbar: 'undo redo',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                />
                <div>
                    {showSaveBtn &&
                        <button type="button" className="btn btn-secondary" onClick={this.onSaveClick} style={{ 'marginRight': '15px' }}>{this.t('essay.save-draft')}</button>
                    }
                    {showSubmitBtn &&
                        <button type="button" className="btn btn-primary" onClick={this.onSubmitBtnClick} style={{ 'marginRight': '15px' }}> {this.t('essay.send-to-teacher')}</button>
                    }
                </div>

            </div>
        );
    }

    tinymceOnPaste = (e: EditorEvent<ClipboardEvent>) => {
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
    }

    render() {
        return Array.from(this.renderer());
    }

    onMceInit(editor: TinyMCEEditor) {
        const self = this;
        self.editor = editor;
        const essay = self.store.essayLoader?.value;
        if (!essay) {
            return;
        }
        const canEdit = essay.StudentSaveAllowed || essay.StudentSubmitAllowed;
        if (canEdit == false) {
            Swal.fire({
                icon: 'info',
                text: this.t('essay.is-readonly', { status: essay.StatusStr }),
                confirmButtonText: this.t('button.ok')
            });
            return;
        }
    }

    onSubmitBtnClick = async () => {
        var self = this;
        // Check text empty
        var editor = self.editor;
        if (!editor) {
            return;
        }
        var editorContent = editor.getContent();
        if (editorContent == '') {
            Swal.fire({
                'icon': 'warning',
                text: this.t('essay.text-is-emty'),
                confirmButtonText: this.t('button.ok')
            });
            return;
        }
        // Confirm send
        const confirmResult = await Swal.fire({
            icon: 'info',
            text: this.t('essay.will-be-sent-for-review-to-teacher'),
            showConfirmButton: true,
            confirmButtonText: this.t('button.yes-send'),
            cancelButtonText: this.t('button.cancel'),
            showCancelButton: true
        });
        if (!confirmResult.isConfirmed) {
            return;
        }

        await self.store.submitEssay(self.props.id, editorContent);
        await Swal.fire({
            'icon': 'success',
            text: this.t('essay.successfully-sent-to-teacher'),
            confirmButtonText: this.t('button.ok')
        });
        routerStore.navigateTo(routes.StudentHomeworkList);
    }

    onSaveClick = async () => {
        var self = this;
        // Check text empty
        var editor = self.editor;
        if (!editor) {
            return;
        }
        var editorContent = editor.getContent();
        if (editorContent == '') {
            Swal.fire({
                icon: 'warning',
                text: this.t('essay.text-is-emty'),
                confirmButtonText: this.t('button.ok')
            });
            return;
        }

        await self.store.saveEssay(self.props.id, editorContent);
        await Swal.fire({
            'icon': 'success',
            text: this.t('essay.successfully-saved'),
            confirmButtonText: this.t('button.ok')
        });
    }
}

export default withTranslation()(EssayStudentScene);